'use client';

// module imports
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

// file imports
import { FeedbackQRCode, FormField, UpdateConfirmationModal } from '@/components';
import { generateEventSlug } from '@/lib/helpers';

const schema = z
  .object({
    title: z.string().min(3, { message: 'Title must be at least 3 characters' }),
    startDate: z.string().refine(
      (value) => {
        if (!value) return false;
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return selectedDate >= today;
      },
      { message: 'Start Date cannot be in the past' }
    ),
    endDate: z.string().refine(
      (value) => {
        if (!value) return false;
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return selectedDate >= today;
      },
      { message: 'End Date cannot be in the past' }
    ),
    venue: z.string().min(3, { message: 'Venue must be at least 3 characters' }),
    numberOfSpeakers: z.coerce.number().min(1, { message: 'Number of Speakers must be at least 1' }),
  })
  .refine(
    (data) => {
      if (!data.startDate || !data.endDate) return true;
      const startDate = new Date(data.startDate);
      const endDate = new Date(data.endDate);
      return endDate >= startDate;
    },
    {
      message: 'End Date must be on or after Start Date',
      path: ['endDate'],
    }
  );

type EventFormData = z.infer<typeof schema>;

interface EventFormProps {
  formData: EventFormData;
  onFormDataChange?: (field: keyof EventFormData, value: string | number) => void;
  onSubmit: (data: Partial<EventFormData>) => void;
  isSubmitting: boolean;
  error: string;
  mode?: 'create' | 'edit';
}

export default function EventForm({ formData, onFormDataChange, onSubmit, isSubmitting, error, mode = 'create' }: EventFormProps) {
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<Partial<EventFormData> | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid, dirtyFields },
  } = useForm<EventFormData>({
    resolver: zodResolver(schema),
    defaultValues: formData,
    mode: 'onChange',
  });

  // Reset form when formData changes (for edit mode)
  useEffect(() => {
    if (mode === 'edit') {
      reset(formData);
    }
  }, [formData, reset, mode]);

  const watchedValues = watch();
  const previewSlug = watchedValues.title && watchedValues.startDate ? generateEventSlug(watchedValues.title, new Date(watchedValues.startDate)) : '';

  const handleFormSubmit = (data: EventFormData) => {
    if (mode === 'edit') {
      // Only send the fields that have been changed
      const changedFields: any = {};

      Object.keys(dirtyFields).forEach((key) => {
        const fieldKey = key as keyof EventFormData;
        if (dirtyFields[fieldKey]) {
          changedFields[fieldKey] = data[fieldKey];
        }
      });

      // Store the form data and show confirmation modal
      setPendingFormData(changedFields);
      setShowUpdateModal(true);
    } else {
      // Create mode - direct submission
      if (onFormDataChange) {
        onFormDataChange('title', data.title);
        onFormDataChange('startDate', data.startDate);
        onFormDataChange('endDate', data.endDate);
        onFormDataChange('venue', data.venue);
        onFormDataChange('numberOfSpeakers', data.numberOfSpeakers);
      }
      onSubmit(data);
    }
  };

  const handleUpdateConfirm = () => {
    if (pendingFormData) {
      onSubmit(pendingFormData);
      setShowUpdateModal(false);
      setPendingFormData(null);
    }
  };

  const handleUpdateCancel = () => {
    setShowUpdateModal(false);
    setPendingFormData(null);
  };

  return (
    <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow flex flex-col gap-4 sm:gap-6 lg:gap-8 w-full md:w-3/4 mx-auto mb-4">
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div className="md:col-span-2">
            <FormField id="title" label={mode === 'edit' ? 'Name of Event' : 'Name of Event (Promotional Name)'} type="text" placeholder={mode === 'edit' ? 'Name of Event' : 'Name of Event (Promotional Name)'} {...register('title')} error={errors.title?.message} required />
          </div>
          <FormField id="startDate" label="Start Date" type="date" {...register('startDate')} error={errors.startDate?.message} required />
          <FormField id="endDate" label="End Date" type="date" {...register('endDate')} error={errors.endDate?.message} required />
          <FormField id="venue" label="Venue" type="text" placeholder="Venue" {...register('venue')} error={errors.venue?.message} />
          <FormField id="numberOfSpeakers" label="No. of Speakers" type="number" placeholder="e.g. 4" {...register('numberOfSpeakers')} error={errors.numberOfSpeakers?.message} />
        </div>

        {previewSlug && <FeedbackQRCode eventSlug={previewSlug} title="Live QR Code" />}

        {error && <div className="text-red-600 text-center mb-4 text-sm sm:text-base">{error}</div>}

        <div className="flex justify-end mt-4 sm:mt-6 lg:mt-8 gap-2 sm:gap-4">
          <button type="submit" className="bg-[#5167b2] text-white font-bold px-4 sm:px-6 lg:px-8 py-2 sm:py-3 rounded-lg sm:rounded-xl shadow hover:bg-[#4158a2] transition disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-sm sm:text-base" disabled={!isValid || isSubmitting || (mode === 'edit' && Object.keys(dirtyFields).length === 0)}>
            {isSubmitting ? 'Saving...' : mode === 'edit' ? 'Update Event' : 'Create Event'}
          </button>
        </div>
      </form>

      {/* Update Confirmation Modal - only shown in edit mode */}
      {mode === 'edit' && <UpdateConfirmationModal isOpen={showUpdateModal} onClose={handleUpdateCancel} onConfirm={handleUpdateConfirm} title="Update Event" message="Are you sure you want to update this event? The changes will be saved permanently." isUpdating={isSubmitting} />}
    </div>
  );
}
